import { Button } from "@/components/ui/button";
import { Nav } from "@/components/landing/Nav";
import { DashboardMock } from "@/components/landing/DashboardMock";
import { TerritorioSection } from "@/components/landing/TerritorioSection";
import { PlanosSection } from "@/components/landing/PlanosSection";
import { SolucoesSection } from "@/components/landing/SolucoesSection";
import { ComparativoSection } from "@/components/landing/ComparativoSection";
import { ComoFuncionaSection } from "@/components/landing/ComoFuncionaSection";
import { ServicePremiumSection } from "@/components/landing/ServicePremiumSection";
import { FloatingWhatsApp } from "@/components/landing/FloatingWhatsApp";
import logo from "@/assets/logo.png";
import iconLogo from "@/assets/icon.png";
import {
  ShieldCheck, Users, MessageSquare, Send, BarChart3, Lock, Sparkles,
  CheckCircle2, MapPin, UserCheck, Workflow, Mail,
  PhoneCall, Globe, ArrowRight, Megaphone, Building2, Network, Briefcase,
  Target, LineChart, Layers, FileCheck2, Activity, Headset, Radar, Server, AppWindow,
} from "lucide-react";

const SealBadge = ({ icon: Icon, label }: { icon: any; label: string }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-white/70 backdrop-blur text-xs font-semibold text-foreground shadow-soft">
    <Icon className="h-3.5 w-3.5 text-accent" />
    {label}
  </div>
);

const SectionTag = ({ children, icon: Icon = Sparkles }: { children: React.ReactNode; icon?: any }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider">
    <Icon className="h-3.5 w-3.5" /> {children}
  </div>
);

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-gradient-hero">
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-border shadow-soft text-xs font-semibold text-primary mb-6">
              <span className="h-2 w-2 rounded-full bg-success animate-pulse-glow" />
              Plataforma + Operação + Inteligência
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground leading-[1.05]">
              Tecnologia, operação e inteligência para{" "}
              <span className="bg-gradient-primary bg-clip-text text-transparent">campanhas políticas.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              A <strong className="text-foreground">VoxCampaign</strong> oferece duas formas de estruturar sua comunicação política: com autonomia da sua equipe ou com operação estratégica assistida.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-gradient-primary text-primary-foreground shadow-glow hover:opacity-95 h-12 px-7 text-base">
                <a href="#solucoes"><AppWindow className="h-4 w-4" /> Conhecer Vox App</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-7 text-base border-primary/20 hover:bg-primary/5">
                <a href="#service"><Headset className="h-4 w-4" /> Conhecer Vox Service</a>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              <SealBadge icon={AppWindow} label="Vox App" />
              <SealBadge icon={Headset} label="Vox Service" />
              <SealBadge icon={Radar} label="Inteligência territorial" />
              <SealBadge icon={ShieldCheck} label="LGPD" />
              <SealBadge icon={Activity} label="Monitoramento 24/7" />
            </div>
          </div>

          <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="absolute -inset-6 bg-gradient-primary opacity-20 blur-3xl rounded-full" />
            <div className="relative animate-float">
              <DashboardMock />
            </div>
            <div className="hidden md:flex absolute -left-6 top-24 items-center gap-2 px-3 py-2 rounded-xl bg-white shadow-elegant border border-border">
              <div className="h-8 w-8 rounded-lg bg-success/15 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-success" />
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground">Performance de leitura</div>
                <div className="text-sm font-bold text-foreground">87% hoje</div>
              </div>
            </div>
            <div className="hidden md:flex absolute -right-4 bottom-12 items-center gap-2 px-3 py-2 rounded-xl bg-white shadow-elegant border border-border">
              <div className="h-8 w-8 rounded-lg bg-highlight/30 flex items-center justify-center">
                <Radar className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground">Operação ativa</div>
                <div className="text-sm font-bold text-foreground">Tempo real</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Diferencial() {
  const pillars = [
    { icon: Headset, t: "Acompanhamento estratégico", d: "Squad sênior dedicado à operação da campanha, do planejamento à execução." },
    { icon: Workflow, t: "Estruturação operacional", d: "Processos, segmentações e fluxos calibrados para a realidade da sua base." },
    { icon: Activity, t: "Monitoramento contínuo", d: "Performance, estabilidade e entregabilidade auditadas em tempo real." },
    { icon: Target, t: "Apoio à decisão", d: "Leituras estratégicas e recomendações baseadas em comportamento real." },
    { icon: LineChart, t: "Escalabilidade controlada", d: "Capacidade de comunicação que cresce no ritmo da campanha — sem rupturas." },
    { icon: ShieldCheck, t: "Continuidade operacional", d: "Infraestrutura preparada para sustentar campanhas em momentos críticos." },
  ];

  return (
    <section id="diferencial" className="py-24 bg-background relative">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl mx-auto text-center">
          <SectionTag>Diferencial</SectionTag>
          <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-foreground">
            Mais do que software.{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">Uma estrutura estratégica para sua campanha.</span>
          </h2>
          <p className="mt-5 text-lg text-muted-foreground">
            A VoxCampaign acompanha campanhas durante toda a operação — apoiando a organização, monitorando performance, sustentando decisões estratégicas e dando escala à comunicação política com estabilidade.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {pillars.map((p) => (
            <div key={p.t} className="group rounded-2xl border border-border bg-card p-6 hover:border-accent/40 hover:shadow-elegant transition-all">
              <div className="h-11 w-11 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 shadow-soft group-hover:scale-110 transition-transform">
                <p.icon className="h-5 w-5 text-primary-foreground" />
              </div>
              <h3 className="text-lg font-bold text-foreground">{p.t}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{p.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl bg-gradient-cta text-primary-foreground p-8 lg:p-10 shadow-elegant relative overflow-hidden">
          <div className="absolute top-0 right-0 h-full w-1/2 bg-gradient-to-l from-accent/30 to-transparent" />
          <div className="relative max-w-3xl">
            <Megaphone className="h-8 w-8 text-highlight mb-4" />
            <p className="text-2xl sm:text-3xl font-bold leading-snug">
              "Campanhas modernas não dependem de ferramentas isoladas. <span className="text-highlight">Dependem de infraestrutura estratégica.</span>"
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Plataforma() {
  const features = [
    { icon: Users, title: "CRM eleitoral integrado", desc: "Visão 360° de eleitores, apoiadores e lideranças em uma única base estratégica." },
    { icon: MapPin, title: "Segmentação territorial", desc: "Bairros, regiões, cidades e zonas eleitorais organizados para a operação." },
    { icon: Workflow, title: "Fluxos operacionais", desc: "Comunicação contínua orquestrada por gatilhos, etapas e jornadas estratégicas." },
    { icon: UserCheck, title: "Gestão de lideranças", desc: "Mapeamento de bases prioritárias e influenciadores comunitários." },
    { icon: MessageSquare, title: "Comunicação multicanal", desc: "WhatsApp, e-mail e canais integrados sob uma única operação." },
    { icon: BarChart3, title: "Painéis estratégicos", desc: "Indicadores políticos e operacionais consolidados em tempo real." },
  ];

  return (
    <section id="plataforma" className="py-24 bg-muted/30 relative overflow-hidden">
      <div className="absolute top-1/2 -translate-y-1/2 -left-40 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
      <div className="mx-auto max-w-7xl px-6 relative">
        <div className="max-w-3xl">
          <SectionTag>Plataforma</SectionTag>
          <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-foreground">
            Uma base operacional que{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">organiza a campanha inteira.</span>
          </h2>
          <p className="mt-5 text-lg text-muted-foreground">
            Cada interação vira inteligência. Cada base, um ativo estratégico. A VoxCampaign concentra a operação política em um ambiente único — preparado para escala, estabilidade e decisão.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              className="group rounded-2xl bg-card border border-border p-6 hover:border-accent/40 hover:shadow-elegant transition-all duration-300"
            >
              <div className="h-11 w-11 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 shadow-soft group-hover:scale-110 transition-transform">
                <f.icon className="h-5 w-5 text-primary-foreground" />
              </div>
              <h3 className="text-lg font-bold text-foreground">{f.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Comunicacao() {
  const channels = [
    { icon: MessageSquare, label: "WhatsApp estratégico", color: "bg-success/15 text-success" },
    { icon: Mail, label: "E-mail institucional", color: "bg-accent/15 text-accent" },
    { icon: Workflow, label: "Fluxos automáticos", color: "bg-primary/10 text-primary" },
    { icon: Target, label: "Segmentação avançada", color: "bg-highlight/30 text-primary" },
  ];
  const benefits = ["Alcance qualificado", "Conversas reais", "Engajamento sustentado", "Continuidade operacional", "Comunicação contínua"];

  return (
    <section id="comunicacao" className="py-24 bg-background">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionTag>Comunicação estratégica em escala</SectionTag>
            <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-foreground">
              Comunicação política operada com{" "}
              <span className="bg-gradient-primary bg-clip-text text-transparent">método e inteligência.</span>
            </h2>
            <p className="mt-5 text-lg text-muted-foreground">
              Personalização contextual, jornadas calibradas, sessões controladas e segmentação por base. A VoxCampaign transforma comunicação política em performance — não em ruído.
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {channels.map((c) => (
                <div key={c.label} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${c.color}`}>
                    <c.icon className="h-4 w-4" />
                  </div>
                  <span className="font-semibold text-foreground text-sm">{c.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-xl border-l-4 border-accent bg-accent/5 p-5">
              <p className="text-sm text-foreground">
                <strong>Arquitetura escalável:</strong> capacidade de comunicação organizada em blocos operacionais, com controle de filas e sessões múltiplas — sustentando campanhas de qualquer porte com estabilidade.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {benefits.map((b) => (
                <span key={b} className="px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-semibold border border-success/20">
                  ✓ {b}
                </span>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-primary opacity-15 blur-3xl rounded-full" />
            <div className="relative rounded-3xl bg-white border border-border shadow-elegant overflow-hidden">
              <div className="bg-primary text-primary-foreground px-5 py-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-success/30 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold text-sm">Operação · Zona Sul</div>
                  <div className="text-[11px] text-primary-foreground/70">Jornada estratégica · Em execução</div>
                </div>
                <div className="ml-auto flex items-center gap-1.5 text-xs bg-success/30 px-2 py-1 rounded-full">
                  <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" /> Ativa
                </div>
              </div>
              <div className="p-5 space-y-3 bg-muted/30 min-h-[280px]">
                {[
                  { side: "in", text: "Olá {{nome}}, somos da equipe e queremos te convidar para a ação no bairro {{bairro}} nesta quinta-feira." },
                  { side: "out", text: "Posso confirmar minha presença?" },
                  { side: "in", text: "Claro! Enviei a confirmação e o endereço completo para você. 📍" },
                ].map((m, i) => (
                  <div key={i} className={`flex ${m.side === "out" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                      m.side === "in" ? "bg-white border border-border text-foreground" : "bg-success text-success-foreground"
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 border-t border-border text-center">
                {[
                  { v: "12.480", l: "Alcance" },
                  { v: "10.852", l: "Leituras" },
                  { v: "3.214", l: "Conversas" },
                ].map((s) => (
                  <div key={s.l} className="p-3">
                    <div className="text-base font-bold text-foreground">{s.v}</div>
                    <div className="text-[11px] text-muted-foreground">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Dashboard() {
  const metrics = [
    { label: "Volume operacional", value: "184.572", icon: Send },
    { label: "Entregabilidade", value: "97,4%", icon: CheckCircle2 },
    { label: "Leitura média", value: "87,1%", icon: BarChart3 },
    { label: "Conversas geradas", value: "21.302", icon: MessageSquare },
    { label: "Variação de engajamento", value: "+24%", icon: LineChart },
    { label: "Apoiadores ativos", value: "9.418", icon: Users },
  ];
  return (
    <section id="dashboard" className="py-24 bg-gradient-to-b from-muted/30 to-background relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 relative">
        <div className="max-w-3xl mx-auto text-center">
          <SectionTag>Painel estratégico</SectionTag>
          <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-foreground">
            Performance política{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">em tempo real.</span>
          </h2>
          <p className="mt-5 text-lg text-muted-foreground">
            Indicadores operacionais, comparativos entre frentes e comportamento das bases — consolidados em painéis pensados para apoiar decisão estratégica de campanha.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {metrics.map((m) => (
            <div key={m.label} className="rounded-2xl bg-card border border-border p-6 hover:shadow-elegant transition-shadow">
              <div className="flex items-center justify-between">
                <div className="h-10 w-10 rounded-xl bg-gradient-primary flex items-center justify-center shadow-soft">
                  <m.icon className="h-5 w-5 text-primary-foreground" />
                </div>
                <span className="text-xs font-semibold text-success">↑ ao vivo</span>
              </div>
              <div className="mt-4 text-3xl font-bold text-foreground">{m.value}</div>
              <div className="text-sm text-muted-foreground">{m.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <DashboardMock />
        </div>
      </div>
    </section>
  );
}

function Seguranca() {
  const items = [
    { icon: Server, title: "Estrutura anti-bloqueio", desc: "Multissessões, controle de filas e camadas de proteção operacional." },
    { icon: ShieldCheck, title: "Conformidade LGPD", desc: "Tratamento de dados auditável e adequado à legislação brasileira." },
    { icon: Lock, title: "Controle de permissões", desc: "Acesso granular por equipe, papel e ambiente de operação." },
    { icon: FileCheck2, title: "Rastreabilidade total", desc: "Logs, auditoria e histórico completo das ações da campanha." },
  ];
  return (
    <section id="seguranca" className="py-24 bg-primary text-primary-foreground relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-accent/30 blur-3xl" />
      <div className="absolute -bottom-40 left-0 h-96 w-96 rounded-full bg-success/20 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-highlight text-xs font-semibold uppercase tracking-wider">
              <ShieldCheck className="h-3.5 w-3.5" /> Estabilidade & compliance
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl font-bold leading-tight">
              Campanhas precisam de{" "}
              <span className="text-highlight">continuidade operacional.</span>
            </h2>
            <p className="mt-5 text-lg text-primary-foreground/80">
              Estabilidade, rastreabilidade e proteção de dados não são opcionais — são pré-requisitos. A VoxCampaign oferece infraestrutura preparada para sustentar a operação política nos momentos mais críticos.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {items.map((i) => (
                <div key={i.title} className="rounded-xl bg-white/5 border border-white/10 p-5 backdrop-blur hover:bg-white/10 transition-colors">
                  <i.icon className="h-6 w-6 text-highlight mb-3" />
                  <h3 className="font-bold">{i.title}</h3>
                  <p className="text-sm text-primary-foreground/70 mt-1">{i.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-accent/40 blur-3xl rounded-full animate-pulse-glow" />
              <div className="relative h-64 w-64 rounded-full bg-gradient-to-br from-accent to-primary-soft border border-white/20 flex items-center justify-center shadow-elegant">
                <ShieldCheck className="h-32 w-32 text-white" strokeWidth={1.2} />
              </div>
              {[
                { Icon: Lock, pos: "top-4 -left-8" },
                { Icon: FileCheck2, pos: "top-10 -right-10" },
                { Icon: UserCheck, pos: "bottom-6 -left-10" },
                { Icon: Layers, pos: "bottom-2 -right-6" },
              ].map(({ Icon, pos }, i) => (
                <div key={i} className={`absolute ${pos} h-14 w-14 rounded-2xl bg-white/10 border border-white/20 backdrop-blur flex items-center justify-center animate-float`} style={{ animationDelay: `${i * 0.4}s` }}>
                  <Icon className="h-6 w-6 text-highlight" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function IdealPara() {
  const items = [
    { icon: Megaphone, title: "Pré-campanhas" },
    { icon: Target, title: "Campanhas eleitorais" },
    { icon: Building2, title: "Mandatos parlamentares" },
    { icon: Users, title: "Mobilização política" },
    { icon: Globe, title: "Comunicação institucional" },
    { icon: Briefcase, title: "Gabinetes" },
    { icon: Network, title: "Coordenações estratégicas" },
    { icon: Sparkles, title: "Marketing político" },
  ];
  return (
    <section className="py-24 bg-muted/30">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl mx-auto text-center">
          <SectionTag>Aplicação</SectionTag>
          <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-foreground">
            Pensado para cada nível da operação política.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.title} className="group flex flex-col items-center text-center p-6 rounded-2xl bg-card border border-border hover:border-accent/40 hover:-translate-y-1 transition-all duration-300 shadow-card">
              <div className="h-14 w-14 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-soft mb-4 group-hover:scale-110 transition-transform">
                <it.icon className="h-6 w-6 text-primary-foreground" />
              </div>
              <span className="font-bold text-foreground">{it.title}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section id="cta" className="py-24 bg-background relative overflow-hidden">
      <div className="mx-auto max-w-6xl px-6">
        <div className="relative rounded-3xl bg-gradient-cta text-primary-foreground p-10 lg:p-16 shadow-elegant overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-accent/40 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-success/20 blur-3xl" />

          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-semibold uppercase tracking-wider text-highlight">
                <Sparkles className="h-3.5 w-3.5" /> Estrutura pronta para sua campanha
              </div>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Opere comunicação política com{" "}
                <span className="text-highlight">inteligência, estabilidade e acompanhamento.</span>
              </h2>
              <p className="mt-5 text-lg text-primary-foreground/85">
                Converse com um especialista e entenda como a VoxCampaign estrutura a operação estratégica da sua campanha.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-highlight text-highlight-foreground hover:opacity-95 h-12 px-7 text-base font-bold shadow-glow">
                  <a href="https://wa.me/5561982415240?text=Ol%C3%A1%2C%20quero%20conhecer%20a%20VoxCampaign." target="_blank" rel="noopener">Agendar demonstração <ArrowRight className="ml-1 h-4 w-4" /></a>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-12 px-7 text-base border-white/30 bg-white/5 text-primary-foreground hover:bg-white/15 hover:text-primary-foreground">
                  <a href="https://wa.me/5561982415240?text=Ol%C3%A1%2C%20quero%20conhecer%20a%20VoxCampaign." target="_blank" rel="noopener">Falar com especialista</a>
                </Button>
              </div>
            </div>

            <div className="rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-8">
              <h3 className="text-lg font-bold text-highlight uppercase tracking-wider">Atendimento direto</h3>
              <div className="mt-6 space-y-5">
                <a href="https://wa.me/5561982415240?text=Ol%C3%A1%2C%20quero%20conhecer%20a%20VoxCampaign." target="_blank" rel="noopener" className="flex items-center gap-4 group">
                  <div className="h-12 w-12 rounded-xl bg-success/30 flex items-center justify-center group-hover:bg-success/50 transition-colors">
                    <PhoneCall className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs text-primary-foreground/70">WhatsApp</div>
                    <div className="text-xl font-bold">(61) 98241-5240</div>
                  </div>
                </a>
                <a href="mailto:contato@viaboots.com.br" className="flex items-center gap-4 group">
                  <div className="h-12 w-12 rounded-xl bg-accent/30 flex items-center justify-center group-hover:bg-accent/50 transition-colors">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs text-primary-foreground/70">E-mail</div>
                    <div className="text-lg font-bold">contato@viaboots.com.br</div>
                  </div>
                </a>
                <a href="https://voxcampaign.online" target="_blank" rel="noopener" className="flex items-center gap-4 group">
                  <div className="h-12 w-12 rounded-xl bg-highlight/30 flex items-center justify-center group-hover:bg-highlight/50 transition-colors">
                    <Globe className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs text-primary-foreground/70">Portal</div>
                    <div className="text-lg font-bold">voxcampaign.online</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border bg-muted/30">
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <img src={logo} alt="Vox Campaign" className="h-12 w-auto" />
            <p className="mt-4 text-muted-foreground max-w-sm italic">
              "Infraestrutura estratégica para campanhas que operam comunicação política com inteligência."
            </p>
            <div className="mt-5 flex gap-2">
              <SealBadge icon={ShieldCheck} label="LGPD" />
              <SealBadge icon={Lock} label="Dados protegidos" />
            </div>
          </div>
          <div>
            <h4 className="font-bold text-foreground mb-4">Estrutura</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="#solucoes" className="hover:text-foreground">Vox App & Service</a></li>
              <li><a href="#comparativo" className="hover:text-foreground">Comparativo</a></li>
              <li><a href="#como-funciona" className="hover:text-foreground">Como funciona</a></li>
              <li><a href="#inteligencia" className="hover:text-foreground">Inteligência</a></li>
              <li><a href="#service" className="hover:text-foreground">Vox Service</a></li>
              <li><a href="#planos" className="hover:text-foreground">Planos</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-foreground mb-4">Contato</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="https://wa.me/5561982415240" target="_blank" rel="noopener" className="hover:text-foreground">WhatsApp: (61) 98241-5240</a></li>
              <li><a href="mailto:contato@viaboots.com.br" className="hover:text-foreground">contato@viaboots.com.br</a></li>
              <li><a href="https://voxcampaign.online" target="_blank" rel="noopener" className="hover:text-foreground">voxcampaign.online</a></li>
              <li><a href="/termos" className="hover:text-foreground">Termos de uso</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-border flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} VoxCampaign. Todos os direitos reservados.</p>
          <div className="flex items-center gap-2">
            <img src={iconLogo} alt="" className="h-5 w-5" />
            <span>Infraestrutura estratégica de comunicação política.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function VoxCampaignLandingPage() {
  return (
    <div className="vox-landing min-h-screen bg-background text-foreground">
      <Nav />
      <main>
        <Hero />
        <SolucoesSection />
        <ComparativoSection />
        <ComoFuncionaSection />
        <Diferencial />
        <Plataforma />
        <div id="inteligencia">
          <TerritorioSection />
        </div>
        <Comunicacao />
        <Dashboard />
        <Seguranca />
        <ServicePremiumSection />
        <PlanosSection />
        <IdealPara />
        <FinalCTA />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
